import type { MetricsLine } from '../../interfaces'
import type { ColumnsType } from 'antd/es/table'

import {
  ApartmentOutlined,
  CalculatorOutlined,
  CaretDownFilled,
  CaretUpFilled,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DollarCircleOutlined,
  DownloadOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  LineChartOutlined,
  ProfileOutlined,
  QuestionCircleOutlined,
  ShareAltOutlined,
  ShopOutlined,
  StopOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  UploadOutlined,
  WarningOutlined,
  SearchOutlined
} from '@ant-design/icons'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, DatePicker, Row, Select, Table, Tooltip, Input } from 'antd'
import cx from 'classnames'
import {
  addDays,
  differenceInCalendarDays,
  differenceInDays,
  endOfDay,
  format,
  formatISO,
  isSameDay,
  startOfDay,
  subDays
} from 'date-fns'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'

import { formatInTimeZone } from 'date-fns-tz'
import { Fragment, useEffect, useMemo, useReducer, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import $api, { API_URL } from '../../API'
import { useCheckRunEvents, useSocketContext } from '../../lib/socket'
import { LiveUpdatesControl } from '../LiveUpdatesControl'
import { fetchData, fetchEventType, fetchFileType } from '../../store/processingDashboard/pd.index'
import {
  selectCount,
  selectData,
  selectIsLoading
} from '../../store/processingDashboard/pd.selector'
import PaginationTable from '../Pagination/PaginationTable'
import Preloader from '../Preloader/Preloader'
import { isAxiosError } from 'axios'

dayjs.extend(isoWeek)

interface Location {
  id: string
  key: string
  name: string
  short_name: string
  location: string
  created_at: string
  updated_at: string
}

/** icons that will replace the coloured text in the first column */
const iconMap: Record<string, React.ComponentType> = {
  /* Check Runs */
  Scheduled: ClockCircleOutlined,
  'On Demand': ThunderboltOutlined,

  /* LuminX */
  Payment: DollarCircleOutlined,
  Voided: StopOutlined,
  Rejected: CloseCircleOutlined,
  Error: WarningOutlined,

  /* Zelis */
  'Sent to': UploadOutlined,
  Received: DownloadOutlined,

  /* Single Warnings */
  'Single Payment Type': WarningOutlined,
  'Single Provider': WarningOutlined,

  /* Check Register */
  'CR Total': CalculatorOutlined,
  Summary: FileTextOutlined,
  Details: ProfileOutlined,
  'Totals mismatch': WarningOutlined,
  'CR Total Error': CloseCircleOutlined,
  'Summary failed': CloseCircleOutlined,
  'Details failed': CloseCircleOutlined,

  /* LIN */
  'Successful LIN Drop-offs': CheckCircleOutlined,
  'Successful SMB Drop-offs': CheckCircleOutlined,
  'Scheduled Drop-offs': ClockCircleOutlined,
  'Successful Registrations': CheckCircleOutlined,
  'Failed LIN Drop-offs': CloseCircleOutlined,
  'Failed SMB Drop-offs': CloseCircleOutlined,
  'Failed Registrations': CloseCircleOutlined,

  /* Check Register additions */
  'Lucent Summary': FileTextOutlined,
  'Lucent Details': ProfileOutlined,
  'Network Summary': ShareAltOutlined,
  'Network Details': ApartmentOutlined,
  'One page': FileDoneOutlined,
  Winsupply: ShopOutlined,
  'Manually Uploaded': DownloadOutlined
}

const FallbackIcon = QuestionCircleOutlined

/* ---------- table columns (bottom detailed list) ---------- */

const columns: ColumnsType<MetricsLine> = [
  {
    dataIndex: 'check_run',
    key: 'checkRun',
    render(checkRun: MetricsLine['check_run']) {
      if (!checkRun) return null

      const date = new Date(checkRun.created_at)

      if (checkRun.type === 'scheduled') {
        return (
          <Tooltip title={`Scheduled Check Run, carried out on ${format(date, 'MM/dd/yyyy')}`}>
            <ClockCircleOutlined className='text-gray-500 opacity-50 hover:opacity-100 transition-opacity translate-x-2 -translate-y-0.5' />
          </Tooltip>
        )
      }

      if (checkRun.type === 'on-demand') {
        return (
          <Tooltip
            title={`Manual (on-demand) Check Run, carried out on ${format(date, 'MM/dd/yyyy')}`}
          >
            <ToolOutlined className='text-gray-500 opacity-50 hover:opacity-100 transition-opacity translate-x-2 -translate-y-0.5' />
          </Tooltip>
        )
      }

      return null
    }
  },
  {
    title: 'Time',
    dataIndex: 'event_date',
    key: 'eventDate',
    render(val: string) {
      return format(new Date(val), 'MM/dd/yyyy HH:mm:ss')
    }
  },
  {
    title: 'Group #',
    dataIndex: ['group', 'number'],
    key: 'groupNumber',
    render(text?: string) {
      return text ?? <span className='text-gray-300'>—</span>
    }
  },
  {
    title: 'Group Name',
    dataIndex: ['group', 'name'],
    key: 'groupName',
    render(text?: string) {
      return text ?? <span className='text-gray-300'>—</span>
    }
  },
  {
    title: 'Location',
    dataIndex: ['location', 'short_name'],
    key: 'location',
    render(text?: string) {
      return text ?? <span className='text-gray-300'>—</span>
    }
  },
  {
    title: 'File Type',
    dataIndex: ['file_type', 'file_type_name'],
    key: 'fileTypeName'
  },
  {
    title: 'File Name',
    dataIndex: 'file_name',
    key: 'fileName',
    render(text?: string) {
      if (text?.trim() === 'null') text = undefined
      return text?.trim() || <span className='text-gray-300'>—</span>
    }
  },
  {
    title: 'Original File Name',
    dataIndex: 'original_filename',
    key: 'originalFileName',
    render(text?: string) {
      if (text?.trim() === 'null') text = undefined
      return text || <span className='text-gray-300'>—</span>
    }
  },
  {
    title: 'Event',
    dataIndex: ['event_type', 'event_type_name'],
    key: 'event',
    render(text: string) {
      return <span className='event-type-cell'>{text}</span>
    }
  }
]

/* ---------- types ---------- */

interface Event {
  event_type_id: number
  file_type_id: number
  events_count: number
  files_count: number
  groups_count: number
}

export interface Metric {
  date: string
  events: Event[]
}

interface Pagination {
  currentPage: number
  pageSize: number
}

interface SearchParams {
  timezone: string
  from: Date
  to: Date
  locationId?: string
  search?: string
}

interface State {
  data: Metric[]
  selection: Selection
  searchParams: SearchParams
  pagination: Pagination
  isLoading: boolean
  error?: Error
}

interface Selection {
  date?: Date
  eventIds: number[]
  fileTypeIds: number[]
}

type Action =
  | { type: 'START_LOADING' }
  | { type: 'RECEIVE_DATA'; payload: Metric[] }
  | { type: 'FAIL'; payload: Error }
  | { type: 'SELECT_PREVIOUS_WEEK' }
  | { type: 'SELECT_NEXT_WEEK' }
  | { type: 'SELECT_DATE_WEEK'; payload: { date: dayjs.Dayjs } }
  | { type: 'SET_SELECTION'; payload: Selection }
  | { type: 'CLEAR_SELECTION' }
  | { type: 'SET_PAGINATION'; payload: Pagination }
  | { type: 'SET_LOCATION_ID'; payload: string }
  | { type: 'CLEAR_LOCATION_ID' }
  | { type: 'SET_SEARCH'; payload: string }
  | { type: 'CLEAR_SEARCH' }

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'START_LOADING':
      return { ...state, isLoading: true }

    case 'RECEIVE_DATA':
      return {
        ...state,
        isLoading: false,
        error: undefined,
        data: action.payload
      }

    case 'FAIL':
      return { ...state, isLoading: false, error: action.payload, data: [] }

    case 'SELECT_PREVIOUS_WEEK':
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          from: subDays(state.searchParams.from, 7),
          to: subDays(state.searchParams.to, 7)
        },
        selection: { date: subDays(state.searchParams.to, 7), eventIds: [], fileTypeIds: [] }
      }

    case 'SELECT_NEXT_WEEK': {
      const { from: currentFrom, to: currentTo } = state.searchParams
      const nextFrom = addDays(currentFrom, 7)
      const nextToCandidate = addDays(currentTo, 7)
      const today = new Date()

      if (nextToCandidate > today) {
        const windowDays = differenceInCalendarDays(currentTo, currentFrom)
        return {
          ...state,
          searchParams: {
            ...state.searchParams,
            from: subDays(today, windowDays),
            to: today
          },
          selection: { date: today, eventIds: [], fileTypeIds: [] }
        }
      }

      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          from: nextFrom,
          to: nextToCandidate
        },
        selection: { date: nextToCandidate, eventIds: [], fileTypeIds: [] }
      }
    }

    case 'SELECT_DATE_WEEK':
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          from: action.payload.date.add(-6, 'day').toDate(),
          to: action.payload.date.toDate()
        },
        selection: { date: action.payload.date.toDate(), eventIds: [], fileTypeIds: [] }
      }

    case 'SET_SELECTION':
      return {
        ...state,
        selection: { ...action.payload },
        pagination: { ...state.pagination, currentPage: 1 }
      }

    case 'CLEAR_SELECTION':
      return {
        ...state,
        selection: { eventIds: [], fileTypeIds: [] },
        pagination: { ...state.pagination, currentPage: 1 }
      }

    case 'SET_PAGINATION':
      return { ...state, pagination: { ...action.payload } }

    case 'SET_LOCATION_ID':
      return {
        ...state,
        searchParams: { ...state.searchParams, locationId: action.payload },
        pagination: { ...state.pagination, currentPage: 1 }
      }

    case 'CLEAR_LOCATION_ID':
      return {
        ...state,
        searchParams: { ...state.searchParams, locationId: undefined },
        pagination: { ...state.pagination, currentPage: 1 }
      }

    case 'SET_SEARCH':
      return {
        ...state,
        searchParams: { ...state.searchParams, search: action.payload },
        pagination: { ...state.pagination, currentPage: 1 }
      }

    case 'CLEAR_SEARCH':
      return {
        ...state,
        searchParams: { ...state.searchParams, search: '' },
        pagination: { ...state.pagination, currentPage: 1 }
      }

    default:
      return state
  }
}

const summary = [
  {
    title: 'Check Runs',
    rows: [
      {
        eventIds: [200],
        fileTypeIds: [200],
        title: 'Scheduled',
        color: '#27AE60'
      },
      {
        eventIds: [201],
        fileTypeIds: [1],
        title: 'On Demand',
        color: '#27AE60'
      }
    ]
  },
  {
    title: 'LuminX',
    rows: [
      {
        eventIds: [1],
        fileTypeIds: [1],
        title: 'Payment',
        description: 'LuminX payment',
        color: '#27AE60'
      },
      {
        eventIds: [137],
        fileTypeIds: [1],
        title: 'Manually Uploaded',
        description: 'Manually uploaded payment files',
        color: '#27AE60'
      },
      { eventIds: [67], fileTypeIds: [1], title: 'Voided', color: '#3599C3' },
      {
        eventIds: [100, 136, 138, 139, 140],
        fileTypeIds: [1],
        title: 'Rejected',
        description: 'Rejected payment file',
        color: '#FA9600'
      },
      {
        eventIds: [6],
        fileTypeIds: [1],
        title: 'Error',
        description: 'Payment parsing error',
        color: '#E71D36'
      },
      {
        eventIds: [1],
        fileTypeIds: [6],
        title: 'CR Total',
        description: 'LuminX CR Total',
        color: '#27AE60'
      },
      {
        eventIds: [137],
        fileTypeIds: [6],
        title: 'Manually Uploaded',
        description: 'Manually uploaded CR totals',
        color: '#27AE60'
      },
      {
        eventIds: [136, 138, 139, 140],
        fileTypeIds: [6],
        title: 'Rejected CR Total',
        description: 'Rejected LuminX CR Total',
        color: '#FA9600'
      },
      {
        eventIds: [6],
        fileTypeIds: [6],
        title: 'CR Total Error',
        description: 'CR Total parsing error',
        color: '#E71D36'
      }
    ]
  },
  {
    title: 'Zelis',
    rows: [
      { eventIds: [2], fileTypeIds: [2], title: 'Sent to', color: '#27AE60' },
      { eventIds: [3], fileTypeIds: [3], title: 'Received', color: '#27AE60' }
    ]
  },
  {
    title: 'Single Warnings',
    rows: [
      {
        eventIds: [301],
        fileTypeIds: [2],
        title: 'Single Provider',
        description: 'Files with only one provider detected',
        color: '#FA9600'
      },
      {
        eventIds: [300],
        fileTypeIds: [3],
        title: 'Single Payment Type',
        description: 'Files with only one payment type detected',
        color: '#FA9600'
      }
    ]
  },
  {
    title: 'Check Register',
    rows: [
      {
        eventIds: [34],
        fileTypeIds: [34],
        title: 'Lucent Summary',
        description: 'Check Register summary',
        color: '#27AE60'
      },
      {
        eventIds: [34],
        fileTypeIds: [35],
        title: 'Lucent Details',
        description: 'Check Register details',
        color: '#27AE60'
      },
      {
        eventIds: [34],
        fileTypeIds: [38],
        title: 'Network Summary',
        description: 'Check Register network summary',
        color: '#27AE60'
      },
      {
        eventIds: [34],
        fileTypeIds: [39],
        title: 'Network Details',
        description: 'Check Register netowrk details',
        color: '#27AE60'
      },
      {
        eventIds: [34],
        fileTypeIds: [36],
        title: 'One page',
        description: 'Check Register details',
        color: '#27AE60'
      },
      {
        eventIds: [34],
        fileTypeIds: [37],
        title: 'Winsupply',
        description: 'Check Register details',
        color: '#27AE60'
      }
    ]
  },
  {
    title: 'LIN',
    rows: [
      {
        eventIds: [202],
        fileTypeIds: [1000],
        title: 'Successful LIN Drop-offs',
        color: '#27AE60'
      },
      {
        eventIds: [202],
        fileTypeIds: [1001],
        title: 'Successful SMB Drop-offs',
        color: '#27AE60'
      },
      {
        eventIds: [133],
        fileTypeIds: [34, 35, 36, 37],
        title: 'Scheduled Drop-offs',
        color: '#FA9600'
      },
      {
        eventIds: [134],
        fileTypeIds: [34, 35, 36, 37],
        title: 'Successful Registrations',
        color: '#27AE60'
      },
      {
        eventIds: [203],
        fileTypeIds: [1000],
        title: 'Failed LIN Drop-offs',
        color: '#E71D36'
      },
      {
        eventIds: [203],
        fileTypeIds: [1001],
        title: 'Failed SMB Drop-offs',
        color: '#E71D36'
      },
      {
        eventIds: [135],
        fileTypeIds: [34, 35, 36, 37],
        title: 'Failed Registrations',
        color: '#E71D36'
      }
    ]
  }
]

/* ---------- utility ---------- */

const areSameArrays = <T,>(a: T[], b: T[]) => a.length === b.length && a.every((v, i) => v === b[i])

export function ProcessingDashBoardSummary(): JSX.Element {
  const data = useSelector(selectData)
  const count = useSelector(selectCount)
  const isLoading = useSelector(selectIsLoading)
  const globalDispatch = useDispatch()

  const [state, dispatch] = useReducer(reducer, {
    data: [],
    selection: { date: new Date(), eventIds: [], fileTypeIds: [] },
    pagination: { currentPage: 1, pageSize: 10 },
    searchParams: {
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC',
      from: subDays(new Date(), 6),
      to: new Date(),
      search: ''
    },
    isLoading: false
  })

  const [hover, setHover] = useState<{
    isoDate?: string
    rowKey?: string
  } | null>(null)

  const [searchInput, setSearchInput] = useState(state.searchParams.search ?? '')
  const [liveUpdatesEnabled, setLiveUpdatesEnabled] = useState(true)

  const queryClient = useQueryClient()
  const { isConnected } = useSocketContext()

  useCheckRunEvents({
    enabled: liveUpdatesEnabled,
    onEvent: () => {
      queryClient.invalidateQueries({ queryKey: ['summary'] })
    }
  })

  useEffect(() => {
    const id = setTimeout(() => {
      if (searchInput !== state.searchParams.search) {
        dispatch({ type: 'SET_SEARCH', payload: searchInput.trim() })
      }
    }, 500)

    return () => clearTimeout(id)
  }, [searchInput, state.searchParams.search])

  useEffect(() => {
    setSearchInput(state.searchParams.search ?? '')
  }, [state.searchParams.search])

  const locationsQuery = useQuery<unknown, unknown, Location[]>({
    queryKey: ['locations'],
    async queryFn() {
      try {
        const r = await $api.get(`/locations`)
        return r.data
      } catch (e) {
        if (isAxiosError(e)) {
          throw new Error(`Request failed with status ${e.status}`)
        }
      }
    }
  })

  /* dates – reversed so newest first */
  const dates = useMemo(() => {
    return Array.from(
      {
        length: differenceInDays(state.searchParams.to, state.searchParams.from) + 1
      },
      (_, i) => addDays(state.searchParams.from, i)
    )
  }, [state.searchParams.from, state.searchParams.to])

  const summaryQuery = useQuery({
    queryKey: ['summary', state.searchParams],

    async queryFn({ queryKey, signal }) {
      const searchParams = queryKey[1] as SearchParams

      const sp = new URLSearchParams({
        timezone: searchParams.timezone,
        from: formatISO(startOfDay(searchParams.from)),
        to: formatISO(endOfDay(searchParams.to))
      })

      if (searchParams.locationId) {
        sp.set('location-id', searchParams.locationId)
      }

      if (searchParams.search && searchParams.search.length > 0) {
        sp.set('search', searchParams.search)
      }

      const response = await $api.get<Metric[]>(`/metrics/getMetricsEventTotals?${sp.toString()}`, {
        signal
      })

      return response.data
    }
  })

  /* initial fetching of dictionaries */
  useEffect(() => {
    globalDispatch(fetchEventType())
    globalDispatch(fetchFileType())
  }, [globalDispatch])

  /* fetch list data whenever selection/pagination changes */
  useEffect(() => {
    globalDispatch(
      fetchData({
        countRows: state.pagination.pageSize,
        currentPage: state.pagination.currentPage - 1,
        fromDate: formatInTimeZone(
          startOfDay(state.selection.date ?? state.searchParams.from),
          'UTC',
          "yyyy-MM-dd'T'HH:mm:ssXXX"
        ),
        toDate: formatInTimeZone(
          endOfDay(state.selection.date ?? state.searchParams.to),
          'UTC',
          "yyyy-MM-dd'T'HH:mm:ssXXX"
        ),
        locationId: state.searchParams.locationId,
        eventTypes: state.selection.eventIds,
        fileTypes: state.selection.fileTypeIds,
        search: state.searchParams.search
      })
    )
  }, [globalDispatch, state])

  /* ------------- render ------------- */

  return (
    <div>
      {/* --- top filters --- */}
      <Card className='w-full rounded-xl' bodyStyle={{ padding: '1rem' }}>
        <Row className='justify-between items-center'>
          <div className='flex gap-2 items-center pl-4 text-zinc-700'>
            <LineChartOutlined className='text-xl' />
            <h2 className='pl-1 text-lg font-medium'>Summary</h2>
          </div>
          <div className='flex gap-8 items-center'>
            <Select
              size='large'
              placeholder='Location'
              className='w-64 rounded-xl'
              loading={locationsQuery.isLoading}
              value={state.searchParams.locationId}
              allowClear
              onChange={v =>
                typeof v === 'string' && dispatch({ type: 'SET_LOCATION_ID', payload: v })
              }
              onClear={() => dispatch({ type: 'CLEAR_LOCATION_ID' })}
              options={(locationsQuery.data ?? []).map(l => ({
                label: l.name,
                value: l.id
              }))}
            />
            <Input
              size='large'
              placeholder='Search by group #, group name or file name'
              className='w-96 rounded-xl'
              allowClear
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              prefix={<SearchOutlined className='text-gray-400' />}
            />
            <DatePicker
              value={dayjs(state.selection.date)}
              onChange={date => {
                if (!date) {
                  return
                }
                dispatch({
                  type: 'SELECT_DATE_WEEK',
                  payload: { date: date }
                })
              }}
              disabledDate={date => date.isAfter(dayjs(new Date()), 'day')}
            />
            <LiveUpdatesControl
              enabled={liveUpdatesEnabled}
              isConnected={isConnected}
              onChange={setLiveUpdatesEnabled}
            />
          </div>
        </Row>
      </Card>

      {/* --- summary grid --- */}
      <div className='relative p-8 mt-4 bg-white rounded-t-3xl shadow-sm'>
        <table className='grid grid-cols-[13.5rem_repeat(7,_minmax(0,_1fr))_13.4rem]'>
          {/* header row with dates */}
          <thead className='contents'>
            <tr className='contents font-normal'>
              <th className='flex items-center justify-end p-2 w-[13.5rem]'>
                <button
                  type='button'
                  className='font-normal p-2 -m-2 text-[#3661EB]'
                  onClick={() => dispatch({ type: 'SELECT_PREVIOUS_WEEK' })}
                >
                  ◀
                </button>
              </th>

              {dates.map(d => {
                const iso = formatISO(d, { representation: 'date' })
                const active =
                  state.selection.date &&
                  isSameDay(d, state.selection.date) &&
                  !state.selection.eventIds.length &&
                  !state.selection.fileTypeIds.length

                const isToday = isSameDay(d, new Date())
                const isYesterday = isSameDay(d, subDays(new Date(), 1))

                return (
                  <th
                    key={`date-${iso}`}
                    className={cx('flex items-center justify-center py-2', {
                      'bg-blue-50': hover?.isoDate === iso
                    })}
                  >
                    <button
                      type='button'
                      className={cx(
                        'flex flex-col items-center p-3 -m-2 w-full transition-colors duration-150',
                        {
                          'hover:bg-blue-50': !active,
                          'bg-[rgba(217,217,217,0.4)]': active
                        }
                      )}
                      onClick={() => {
                        if (active) return dispatch({ type: 'CLEAR_SELECTION' })
                        dispatch({
                          type: 'SET_SELECTION',
                          payload: { date: d, eventIds: [], fileTypeIds: [] }
                        })
                      }}
                    >
                      <span
                        className={cx('text-xs font-semibold uppercase', {
                          'text-blue-700': isToday,
                          'text-gray-700': !isToday
                        })}
                      >
                        {isToday ? 'Today' : isYesterday ? 'Yesterday' : format(d, 'eeee')}
                      </span>
                      <span
                        className={cx('text-xs', {
                          'text-blue-700': isToday,
                          'text-gray-500': !isToday
                        })}
                      >
                        {format(d, 'MM/dd/yyyy')}
                      </span>
                    </button>
                  </th>
                )
              })}

              <th className='flex justify-start items-center p-2'>
                <button
                  type='button'
                  className={cx(
                    'font-normal p-2 -m-2',
                    dayjs(state.searchParams.to).isAfter(dayjs(), 'day') ||
                      dayjs(state.searchParams.to).isSame(dayjs(), 'day')
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-[#3661EB]'
                  )}
                  onClick={() => {
                    dispatch({ type: 'SELECT_NEXT_WEEK' })
                  }}
                  disabled={
                    dayjs(state.searchParams.to).isAfter(dayjs(), 'day') ||
                    dayjs(state.searchParams.to).isSame(dayjs(), 'day')
                  }
                >
                  ▶
                </button>
              </th>
            </tr>
          </thead>

          <tbody className='contents'>
            {summary.map(grp => (
              <Fragment key={`group-${grp.title}`}>
                <tr className='contents'>
                  {[...Array(dates.length + 2)].map((_, colIndex) => {
                    const isFirst = colIndex === 0
                    const date = dates[colIndex - 1] ?? null
                    const iso =
                      date && colIndex > 0 && colIndex <= dates.length
                        ? formatISO(date, {
                            representation: 'date'
                          })
                        : null

                    const isHovered = hover?.isoDate === iso

                    return (
                      <td
                        key={`group-${grp.title}-col-${colIndex}`}
                        className={cx('border-b p-2', {
                          'bg-gray-50': isHovered || hover?.isoDate === iso,
                          'whitespace-nowrap font-semibold text-lg mt-6': isFirst
                        })}
                        style={isFirst ? { color: '#3661EB' } : undefined}
                      >
                        {isFirst ? grp.title : null}
                      </td>
                    )
                  })}
                </tr>

                {/* rows with metrics */}
                {grp.rows.map(row => {
                  const rowKey = `${row.eventIds.join('_')}-${row.fileTypeIds.join('_')}`
                  const rowSelected =
                    !state.selection.date &&
                    areSameArrays(state.selection.eventIds, row.eventIds) &&
                    areSameArrays(state.selection.fileTypeIds, row.fileTypeIds)

                  const Icon = iconMap[row.title] ?? FallbackIcon

                  return (
                    <tr key={`row-${grp.title}-${rowKey}`} className='contents'>
                      <td
                        className={cx('border-b w-[13.5rem] p-0', {
                          'bg-blue-50': hover?.rowKey === rowKey
                        })}
                        onMouseEnter={() => setHover({ rowKey })}
                        onMouseLeave={() => setHover(null)}
                      >
                        <button
                          type='button'
                          title={row.description}
                          className={cx(
                            'flex w-full h-full items-center gap-2 font-semibold text-left text-sm p-2',
                            {
                              'hover:bg-blue-50': !rowSelected,
                              'bg-[rgba(217,217,217,0.4)]': rowSelected
                            }
                          )}
                          onClick={() => {
                            if (rowSelected) return dispatch({ type: 'CLEAR_SELECTION' })
                            dispatch({
                              type: 'SET_SELECTION',
                              payload: {
                                eventIds: [...row.eventIds],
                                fileTypeIds: [...row.fileTypeIds]
                              }
                            })
                          }}
                        >
                          <Icon style={{ color: row.color }} />
                          <span>{row.title}</span>
                        </button>
                      </td>

                      {dates.map(d => {
                        const iso = formatISO(d, { representation: 'date' })
                        const metric = summaryQuery.data?.find(m => m.date === iso)

                        const events =
                          metric?.events.filter(
                            e =>
                              row.eventIds.includes(e.event_type_id) &&
                              row.fileTypeIds.includes(e.file_type_id)
                          ) || []

                        const count = events.reduce((acc, e) => {
                          const c = [200, 201].includes(e.event_type_id)
                            ? e.groups_count
                            : e.events_count
                          return acc + c
                        }, 0)

                        const files = events.reduce((a, e) => a + e.files_count, 0)

                        const cellSelected =
                          state.selection.date &&
                          isSameDay(d, state.selection.date) &&
                          areSameArrays(state.selection.eventIds, row.eventIds) &&
                          areSameArrays(state.selection.fileTypeIds, row.fileTypeIds)

                        const cellKey = `cell-${rowKey}-${iso}`
                        const baseProps = {
                          onMouseEnter: () => setHover({ isoDate: iso, rowKey }),
                          onMouseLeave: () => setHover(null),
                          className: cx('text-center border-b p-0', {
                            'bg-gray-50': hover?.isoDate === iso || hover?.rowKey === rowKey,
                            'bg-blue-100': hover?.isoDate === iso && hover?.rowKey === rowKey
                          })
                        }

                        if (!events.length || !count)
                          return (
                            <td key={cellKey} {...baseProps}>
                              <Tooltip
                                style={{
                                  pointerEvents: 'none'
                                }}
                                className='cursor-default'
                                title={
                                  <div>
                                    <div className='text-xs text-gray-300 uppercase'>
                                      {format(d, 'eeee, MM/dd/yyyy')}
                                    </div>
                                    <div>
                                      {grp.title} - {row.title}
                                    </div>
                                  </div>
                                }
                                mouseEnterDelay={0}
                                mouseLeaveDelay={0}
                                destroyOnHidden
                                getPopupContainer={trigger => trigger.parentElement!}
                              >
                                <div className='px-1 py-2 text-gray-300'>-</div>
                              </Tooltip>
                            </td>
                          )

                        return (
                          <td key={cellKey} {...baseProps}>
                            <Tooltip
                              style={{
                                pointerEvents: 'none'
                              }}
                              className='cursor-default'
                              title={
                                <div>
                                  <div className='text-xs text-gray-300 uppercase'>
                                    {format(d, 'eeee, MM/dd/yyyy')}
                                  </div>
                                  <div>
                                    {grp.title} - {row.title}
                                  </div>
                                </div>
                              }
                              mouseEnterDelay={0}
                              mouseLeaveDelay={0}
                              destroyOnHidden
                              getPopupContainer={trigger => trigger.parentElement!}
                            >
                              <button
                                type='button'
                                title={
                                  files
                                    ? `${count} ${count === 1 ? 'group' : 'groups'} in ${files} ${files === 1 ? 'file' : 'files'}`
                                    : undefined
                                }
                                className={cx('w-full py-2 px-2 text-center cursor-pointer', {
                                  'hover:bg-blue-50': !cellSelected,
                                  'bg-[rgba(217,217,217,0.4)]': cellSelected
                                  // "bg-blue-100": hover?.isoDate === iso && hover?.rowKey === rowKey,
                                })}
                                onClick={() => {
                                  if (cellSelected) return dispatch({ type: 'CLEAR_SELECTION' })
                                  dispatch({
                                    type: 'SET_SELECTION',
                                    payload: {
                                      date: d,
                                      eventIds: [...row.eventIds],
                                      fileTypeIds: [...row.fileTypeIds]
                                    }
                                  })
                                }}
                              >
                                {count}
                              </button>
                            </Tooltip>
                          </td>
                        )
                      })}

                      {/* spacer after date columns */}
                      <td
                        className={cx('border-b p-0 w-[13.5rem]', {
                          'bg-blue-50': hover?.rowKey === rowKey
                        })}
                        onMouseEnter={() => setHover({ rowKey })}
                        onMouseLeave={() => setHover(null)}
                      >
                        <button
                          type='button'
                          title={row.description}
                          className={cx(
                            'flex w-full h-full items-center justify-end gap-2 p-2 font-semibold box-border text-sm ',
                            {
                              'hover:bg-blue-50': !rowSelected,
                              'bg-[rgba(217,217,217,0.4)]': rowSelected
                            }
                          )}
                          onClick={() => {
                            if (rowSelected) return dispatch({ type: 'CLEAR_SELECTION' })
                            dispatch({
                              type: 'SET_SELECTION',
                              payload: {
                                eventIds: [...row.eventIds],
                                fileTypeIds: [...row.fileTypeIds]
                              }
                            })
                          }}
                        >
                          <span className={'whitespace-nowrap'}>{row.title}</span>
                          <Icon style={{ color: row.color }} />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* --- details list --- */}
      <div className='pt-4 bg-white rounded-b-3xl shadow-sm'>
        <Table
          size='middle'
          className='table-custom no-rounding no-padding'
          rowKey={(rec: MetricsLine) =>
            [
              rec.id,
              rec.file_name,
              rec.file_type.id,
              rec.event_type.id,
              rec.group?.number,
              rec.event_date
            ]
              .filter(Boolean)
              .join('-')
          }
          columns={columns}
          dataSource={data.slice()}
          pagination={false}
          loading={{ indicator: <Preloader />, spinning: isLoading }}
          expandable={{
            rowExpandable: rec =>
              [1, 2, 3, 6, 34, 36, 67, 100, 200, 203, 135].includes(rec.event_type.id),
            expandedRowRender: (rec, i, indent, expanded) => (
              <ExpandedRow record={rec} index={i} indent={indent} expanded={expanded} />
            )
          }}
          onRow={row =>
            row.event_type.event_type_category === 'Negative'
              ? { className: 'negative-event-type' }
              : {}
          }
        />

        <PaginationTable
          changePage={(currentPage, pageSize) =>
            dispatch({
              type: 'SET_PAGINATION',
              payload: { currentPage, pageSize }
            })
          }
          total={count}
          defaultPageSize={state.pagination.pageSize}
          pageOptions={[10, 25, 50, 100]}
          current={state.pagination.currentPage}
        />
      </div>
    </div>
  )
}

interface ExpandedRowProps {
  record: MetricsLine
  index: number
  indent: number
  expanded: boolean
}

function ExpandedRow({ record, expanded }: ExpandedRowProps): JSX.Element | null {
  const [showStack, setShowStack] = useState(false)
  const token = window.localStorage.getItem('token')
  if (!expanded || !token) return null

  const link = (name: string) =>
    `${API_URL}/files/${encodeURIComponent(name)}?event-type-id=${encodeURIComponent(
      record.event_type.id
    )}&file-type-id=${encodeURIComponent(record.file_type.id)}&token=${encodeURIComponent(token)}`

  return (
    <div className='p-4'>
      {/* download link if available */}
      {(() => {
        switch (record.event_type.id) {
          case 1:
          case 2:
          case 3:
          case 6:
          case 34:
          case 100:
            return record.file_name ? <a href={link(record.file_name)}>{record.file_name}</a> : null
          case 36:
            return record.file_name ? (
              <a href={link(record.file_name)}>{record.file_name.split('/').pop()}</a>
            ) : null
          case 67:
          case 200:
            return record.original_filename ? (
              <a href={link(record.original_filename)}>{record.original_filename}</a>
            ) : null
          default:
            return null
        }
      })()}

      {record.error && ('message' in record.error || 'stackTrace' in record.error) && (
        <div className='flex flex-col gap-2 justify-start items-start mt-2'>
          <button
            className='font-mono text-sm'
            type='button'
            onClick={() => {
              setShowStack(v => !v)
            }}
          >
            Error:{' '}
            <span className='underline underline-offset-4'>
              {('message' in record.error && record.error.message) || 'Unknown'}
            </span>
            {((record.error && 'activityType') || 'stack' in record.error) && (
              <>
                {!showStack && <CaretDownFilled className='ml-2 text-gray-400' />}

                {showStack && <CaretUpFilled className='ml-2 text-gray-500' />}
              </>
            )}
          </button>

          {showStack && 'stack' in record.error && typeof record.error.stack === 'string' && (
            <div className='font-mono text-xs text-gray-600 whitespace-pre-wrap'>
              {record.error.stack}
            </div>
          )}

          {showStack && record.error && 'stackTrace' in record.error && (
            <div className='font-mono text-xs text-gray-600 whitespace-pre-wrap'>
              Cause: {JSON.stringify(record.error.stackTrace, null, 2)}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
